/**
 * The dev domain currently in use.
 *
 * @since 5.16.1
 */
export const DEV_DOMAIN = 'app.optinmonster.test';

// Setup some variables which will be minified.
// Saves some overall bytes in the build size.
const protocol = 'https://';
const omTld = 'optinmonster.com';
const mcTld = 'monstercampaigns.com';
const cDomainAppJsPath = `${protocol}{cDomain}/app/js/`;
const aDomainApiUrl = `${protocol}{env-api}{aDomain}/`;
const devURL = `${protocol}${DEV_DOMAIN}`;
const devApiJsURL = `${devURL}/wp-content/optinmonster-api-js/`;

/**
 * The global variable that is added to the window object for OptinMonster.
 *
 * @since 5.0.0
 */
export const GLOBAL_OM = '_omapp';

/**
 * An affiliate URL for use if there is none provided in the API requests.
 *
 * @since 5.0.0
 */
export const AFFILIATE_URL =
	`https://${omTld}/powered-by/?utm_medium=badge&utm_campaign=powered-by&utm_source=` +
	encodeURIComponent(window.location.href);

/**
 * Allowed hosts.
 *
 * @since 5.0.0
 */
export const ALLOWED_HOSTS = [
	'.test',
	'.local',
	'local.',
	'staging.',
	'localhost',
	'127.0.0.1',
	'bigcommerce.com',
	'blogspot.com',
	'jimdo.com',
	'joomla.com',
	'pswebstore.com',
	'myshopify.com',
	'squarespace.com',
	'tumblr.com',
	'volusion.com',
	'weebly.com',
	'rmkr.net',
];

/**
 * The domain used throughout the app.
 *
 * @since 5.0.0
 */
export const API_DOMAIN = 'omappapi.com';

/**
 * The domain specifically used for API requests from the WordPress plugin.
 *
 * @since 5.18.42
 */
export const WP_DOMAIN = 'omwpapi.com';

/**
 * The domain specifically used for MonsterCampaigns.
 *
 * @since 5.21.17
 */
export const MC_DOMAIN = `app.${mcTld}`;

/**
 * The OM Domains to use instead of the API_DOMAIN.
 *
 * @since 5.3.15
 */
export const OM_DOMAINS = [mcTld, WP_DOMAIN, omTld, 'optinforms.com'];

/**
 * The an array of scripts to parse in the Scripts class.
 *
 * @since 5.16.1
 *
 * @returns {Object} An object of script URLs.
 */
export const SCRIPTS = ['analytics', 'adblock', 'geolocation', 'fonts', 'soundEffects', 'recaptcha'];

/**
 * The path for the adblock script.
 *
 * @returns {string} The script path.
 */
export const ADBLOCK_SCRIPT_PATH = 'https://a.optinmonster.com/app/js/prebid-ads.js';

/**
 * Url's needed throughout the application.
 *
 * @since 5.16.1
 */
export const URLS = {
	base: cDomainAppJsPath,
	css: `${cDomainAppJsPath}api.min.css`,
	cssDev: `${devApiJsURL}dist/Api.css`,
	debug: `${protocol}{cDomain}/debug/js/ApiDebug.js`,
	debugDev: `${devApiJsURL}dist/ApiDebug.js`,
	lsi: `${cDomainAppJsPath}lsi.min.js`,
	lsiDev: `${devURL}/wp-content/optinmonster-live-site-inspector/dist/lsi.min.js`,
	api: `${devApiJsURL}dist/Api.js`,
	embed: `${aDomainApiUrl}v2/embed/`,
	optin: `${aDomainApiUrl}v2/optin/`,
	revenue: `${aDomainApiUrl}v2/revenue/`,
	verify: `${aDomainApiUrl}v2/sites/verify/`,
	adblock: ADBLOCK_SCRIPT_PATH,
	geolocation: `${aDomainApiUrl}v3/geolocate/json/{ip}`,
	fontAwesome: `${cDomainAppJsPath}font-awesome/4.7.0/css/font-awesome.css`,
	soundEffects: `${cDomainAppJsPath}soundeffects/soundeffects.lib.js`,
	soundEffectsDev: `${devApiJsURL}dependencies/soundeffects/dist/soundeffects.lib.js`,
	recaptcha: `${protocol}www.google.com/recaptcha/api.js?render={recaptchaSiteKey}`,
	impressions: `${protocol}{zDomain}/v3/i`,
	conversions: `${protocol}{zDomain}/v3/c`,
	monsterCampaigns: `${protocol}{mDomain}/c/`,
	customCampaigns: `${protocol}{aDomain}/c/`,
};

/**
 * Strings used as translations
 *
 * @since 5.25.38
 */
export const STRINGS = {};
